import styled, { useTheme } from "styled-components";
import { IconPhone } from "suomifi-icons";
import type { Tukipyynto } from "../types";
import { useTranslation } from "react-i18next";

/**
 * Tukipyynnön laitetiedot — laitemalli + OS + sovellusversio.
 *
 * Workshop 1.6.2026 C7: tieto välitetään automaattisesti mobiilisovelluksesta
 * (tekninen toteutus selvitettävänä). Prototyypissä mock-datasta. Virkailija
 * näkee mistä laitteesta tukipyyntö tuli — auttaa NFC-/MRZ-/kasvokuva-
 * ongelmien diagnostiikassa (sovellusversio, käyttöjärjestelmä, mallisarja).
 */

interface Props {
  laite: NonNullable<Tukipyynto["laite"]>;
}

const Wrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.m};
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  & > svg {
    width: 1.75rem;
    height: 1.75rem;
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Malli = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBold};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const MetaRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.m};
  margin-top: ${({ theme }) => theme.spacing.xxs};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

const MetaLabel = styled.span`
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const MetaValue = styled.span`
  color: ${({ theme }) => theme.colors.blackBase};
  font-family: "Courier New", monospace;
`;

export default function TukipyyntoLaite({ laite }: Props) {
  const { t } = useTranslation("common");
  const theme = useTheme();

  return (
    <Wrap>
      <IconWrap>
        <IconPhone color={theme.colors.brandBase} aria-hidden />
      </IconWrap>
      <Body>
        <Malli>{laite.malli}</Malli>
        <MetaRow>
          <MetaItem>
            <MetaLabel>OS:</MetaLabel>
            <MetaValue>{laite.os}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{t("sovellusversio")}:</MetaLabel>
            <MetaValue>{laite.sovellusversio}</MetaValue>
          </MetaItem>
        </MetaRow>
      </Body>
    </Wrap>
  );
}
